import { Badge, Group, Paper, Text } from "@mantine/core";
import { useTranslation } from "react-i18next";

import type { TeamDailyPresence } from "@msc/shared";
import { anzeigeName } from "../../lib/anzeigeName.js";

/** `YYYY-MM-DD` → `TT.MM.` für die kompakte Anzeige. */
function ddmm(iso: string): string {
  const [, m, d] = iso.split("-");
  return `${d}.${m}.`;
}

/**
 * Abwesenheits-Leiste ganz oben: wer ist **heute** weg (prominent) und wer in
 * den nächsten zwei Wochen. Quelle ist `employee_absence_days` (nächtlich aus
 * Personio) — ohne Grund, nur Tag und Anteil.
 */
export function PresenceBar({
  presence,
  boardDate,
}: {
  presence: TeamDailyPresence;
  boardDate: string;
}) {
  const { t } = useTranslation();


  const today = presence.members
    .map((m) => ({
      name: anzeigeName(m.fullName, boardDate),
      day: m.days.find((d) => d.date === boardDate),
    }))
    .filter((x) => x.day !== undefined);

  const upcoming = presence.members
    .map((m) => ({
      name: anzeigeName(m.fullName, boardDate),
      days: m.days.filter((d) => d.date > boardDate),
    }))
    .filter((x) => x.days.length > 0);

  return (
    <Paper withBorder p="xs">
      <Group gap="xs" wrap="wrap">
        <Text size="xs" fw={700} c="dimmed" tt="uppercase">
          {t("pages.teamDaily.presenceToday")}
        </Text>
        {today.length === 0 ? (
          <Text size="sm" c="dimmed">
            {t("pages.teamDaily.presenceAllPresent")}
          </Text>
        ) : (
          today.map((x) => (
            <Badge key={x.name} size="sm" color="gray" variant="light">
              {x.name}
              {x.day && x.day.dayFraction < 1 ? " ½" : ""}
            </Badge>
          ))
        )}
      </Group>

      {upcoming.length > 0 && (
        <Group gap="lg" wrap="wrap" mt={6}>
          <Text size="xs" fw={700} c="dimmed" tt="uppercase">
            {t("pages.teamDaily.presenceUpcoming")}
          </Text>
          {upcoming.map((u) => (
            <Text key={u.name} size="xs">
              <Text span fw={600}>
                {u.name}:
              </Text>{" "}
              {u.days.map((d) => ddmm(d.date)).join(", ")}
            </Text>
          ))}
        </Group>
      )}
    </Paper>
  );
}
